/**
 * The village plan page (rebuild M2, 21 Oct 2026; the agreed mock-up's j1-hub and j2-hub).
 *
 * Where the module opens once a plan is chosen: why they are moving and what it costs, the contract, the
 * chosen plan with its numbers against staying put, the advice document and the next step (legal advice on
 * the contract), the other models the family can start, and the ways on. Every figure is the chosen card's
 * own, as it was chosen.
 *
 * ON THE SHARED PIECES (owner, 22 Oct 2026: "Yes i want this all consistent"). The card, the document and next
 * step, "Model something else" and the links are the ones every module's plan page uses
 * (components/strategy/ChosenPlanPage.tsx), in aged care's shape: the village's own facts — the Age Pension,
 * what each year leaves over or is short by, and the village fees with what leaving would cost — are the
 * card's figure strip, not tables of their own.
 */
import React from 'react';
import { ArrowLeftRight, BookOpen, Hammer, Pencil } from 'lucide-react';
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { whyMoveWords, parseList } from './rvCopy.ts';
import { rvCardRunLine, type ChosenRvPlan } from './rvChosenPlan.ts';
import AnnuityFamilyPanel from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { PlanOutOfDateBanner } from '../../components/strategy/PlanOutOfDateBanner.tsx';
import { ChosenPlanCard, DocumentAndNextStep, ModelSomethingElse, PlanPageLinks, type PlanFigure } from '../../components/strategy/ChosenPlanPage.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

export interface RvHubProps {
    factFind: DraftFactFind;
    plan: ChosenRvPlan;
    /** How many units have a saved plan. */
    unitCount: number;
    /** The village's move-in date, if one is set. */
    entryDate?: string;
    onChangeContract: () => void;
    onAnotherUnit: () => void;
    /** A new model from scratch: step 1 empty, the questions asked again, nothing carried over. */
    onStartFresh: () => void;
    onAllPlans: () => void;
    onExplain: () => void;
    onRisks: () => void;
    onAdvice: () => void;
    onBuild: () => void;
    /** "Build a plan yourself": a blank plan for the same unit (25 Oct 2026). */
    onBuildNew?: () => void;
    /** Why the chosen plan's figures are out of date (chosenPlanRefresh.ts), or null (25 Oct 2026). */
    outOfDate?: string | null;
    onGuide: () => void;
    onAssumptions: () => void;
    onHome: () => void;
}

const money = (n: number) => formatCurrency(Math.round(n));
const signed = (n: number) => `${n >= 0 ? '+' : '−'}${money(Math.abs(n))}`;
const dateWords = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : null);
const link = 'font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900';

const RvHub = ({ factFind, plan, unitCount, entryDate, onChangeContract, onAnotherUnit, onStartFresh, onAllPlans, onExplain, onRisks, onAdvice, onBuild, onBuildNew, outOfDate = null, onGuide, onAssumptions, onHome }: RvHubProps) => {
    const v = householdVoice(factFind);
    const card = plan.inputs!.card!;
    const goals = (factFind?.goals || {}) as { rvWhyMove?: string; rvWhyMoveOther?: string };
    const reasons = whyMoveWords(parseList(goals.rvWhyMove), goals.rvWhyMoveOther);
    const base = card.baseline;
    const delta = base ? card.plan.legacy - base.legacy : null;
    const yrs = `${card.horizonYears} ${card.horizonYears === 1 ? 'year' : 'years'}`;
    const village = card.village.name || 'the village';
    const cash = (n: number) => (n < 0 ? `short ${money(-n)}` : `${money(n)} to spare`);
    const feesWords = card.village.fees > 0 ? `${money(card.village.fees)} a ${card.village.feesFrequency === 'weekly' ? 'week' : 'month'} in fees` : 'no ongoing fees';
    const when = entryDate ? `Moving on ${dateWords(entryDate)}` : 'Moving now';
    const pensionChange = base ? card.plan.pension - base.pension : null;
    const manual = card.origin === 'manual';
    // This plan's own runway line only — a hand-built card from before it could save one shows none (rvCardRunLine).
    const runLine = rvCardRunLine(plan);

    // The strip: what the plan pays and leaves each year against staying put, and what the village costs
    // (aged care's rule: a cost or a shortfall stays plain; only money left over is green).
    const figures: PlanFigure[] = [
        {
            label: 'Age Pension',
            value: money(card.plan.pension),
            unit: 'a year',
            note: pensionChange == null ? undefined
                : Math.abs(pensionChange) >= 1
                    ? <span className={`font-semibold ${pensionChange > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{signed(pensionChange)} a year vs staying put</span>
                    : 'the same as staying put',
            testId: 'rv-hub-pension',
        },
        {
            label: card.plan.cashflow < 0 ? 'Each year, short by' : 'Left over each year',
            value: money(Math.abs(card.plan.cashflow)),
            tone: card.plan.cashflow < 0 ? 'plain' : 'good',
            note: base ? `staying put: ${cash(base.cashflow)}` : undefined,
            testId: 'rv-hub-cashflow',
        },
        {
            label: 'Village fees',
            value: money(card.village.annualOngoing),
            unit: 'a year',
            note: `departure fee ${money(card.village.departureFee)} after ${yrs} · ${money(card.village.backAfter)} back`,
            testId: 'rv-hub-fees',
        },
    ];

    return (
        <div className="mx-auto max-w-5xl space-y-4" data-testid="rv-hub">
            <div>
                <p className="text-[13px] text-slate-500"><button type="button" onClick={onHome} className="underline decoration-dotted underline-offset-2 hover:text-slate-800">Home</button> › Retirement village</p>
                <h1 className="mt-2 text-[32px] font-bold leading-tight tracking-tight text-brand-navy" data-testid="rv-hub-title">{v.you ? 'Your village plan' : `${v.Possessive} village plan`} — {village}</h1>
                {(reasons || delta != null) && (
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-900">
                        {reasons && <><strong>Why {v.you ? "you're" : `${v.they} ${v.are}`} moving:</strong> {reasons}. </>}
                        {delta != null && (delta < 0
                            ? <>The move costs the family {money(-delta)} over about {yrs} against staying put{card.origin === 'engine' ? '; this is the plan chosen from the ways we tested.' : '.'}</>
                            : <>The move leaves the family {money(delta)} more than staying put over about {yrs}.</>)}
                    </p>
                )}
                {/* Aged care's header line: the facts the plan rests on, then the three ways to change them. The
                    entry payment is said here, so the card's body does not say it again. */}
                <p className="mt-1 text-[15px] text-slate-600">
                    {when}; {money(card.village.entryFee)} entry payment, {feesWords}.{' '}
                    <button type="button" onClick={onChangeContract} className={link} data-testid="rv-hub-change-contract">Change these</button>
                    {' · '}
                    <button type="button" onClick={onAnotherUnit} className={link} data-testid="rv-hub-another-unit">Model another village or unit</button>
                    {' · '}
                    <button type="button" onClick={onStartFresh} className={link} data-testid="rv-hub-start-fresh-header">Start a new model from scratch</button>.
                </p>
            </div>

            {/* A chosen plan whose figures are out of date says so, with a way to choose again (25 Oct 2026). */}
            <PlanOutOfDateBanner reason={outOfDate} onChooseAgain={onAllPlans} testId="rv-hub-out-of-date" />
            <ChosenPlanCard
                chosenOn={dateWords(plan.createdAt)}
                builtByYou={manual}
                title={card.words.title}
                sub={card.words.sub}
                estate={card.plan.legacy}
                years={card.horizonYears}
                estateDelta={delta}
                baseWord="staying put"
                figures={figures}
                onExplain={onExplain}
                onRisks={onRisks}
                onAllPlans={onAllPlans}
                testIdPrefix="rv-hub"
            >
                {/* When the money runs out, as the card said it when chosen (runwayCardLine, 22 Oct 2026). A card
                    chosen before then did not keep its rows, so it has no line to show. */}
                {runLine && (
                    <p className="text-[14px] leading-relaxed text-slate-800" data-testid="rv-hub-runline"><strong className="text-slate-900">When the money runs out:</strong> {runLine}</p>
                )}
                {card.rentNet > 0 && (
                    <p className={`${runLine ? 'mt-2 ' : ''}text-[14px] leading-relaxed text-slate-800`} data-testid="rv-hub-rent"><strong className="text-slate-900">Rent from the home:</strong> {money(card.rentNet)} a year, after its costs.</p>
                )}
                {/* What the family receives from the guaranteed income, as the card showed it when chosen (E4/M4). */}
                {card.family && card.family.rows.length > 0 && (
                    <div className={runLine || card.rentNet > 0 ? 'mt-4' : ''}>
                        <AnnuityFamilyPanel
                            factFind={factFind}
                            personName={card.family.personName}
                            amount={card.family.amount}
                            kind={card.family.kind}
                            incomeAYear={card.family.incomeAYear}
                            rows={null}
                            frozen={card.family.rows}
                            compareNote={card.family.compareNote}
                            horizonYears={card.horizonYears}
                            testId="rv-hub-family-panel"
                        />
                    </div>
                )}
            </ChosenPlanCard>

            <DocumentAndNextStep
                documentState={plan.soaSnapshot ? 'prepared' : 'not prepared yet'}
                documentLine="The written record of this plan and this village's contract."
                documentAction={<button type="button" onClick={onAdvice} className={link} data-testid="rv-hub-advice">Get the advice document</button>}
                nextTitle="legal advice on the contract"
                nextLine="Take the disclosure statement to a solicitor before signing; a cooling-off period applies."
                nextAction={<button type="button" onClick={onHome} className={link} data-testid="rv-hub-home">Full action plan on Home</button>}
            />

            {/* Model something else (aged care's, 19 Oct 2026): each way says what it keeps; this plan stays chosen. */}
            <ModelSomethingElse
                testId="rv-hub-model-something-else"
                options={[
                    {
                        title: 'Another village or unit, same answers',
                        body: `Keeps ${v.possessive} answers to the questions and the plan settings. Only the village and its contract are new.${unitCount > 1 ? ` ${unitCount} villages or units modelled so far.` : ''}`,
                        action: 'Model another village or unit',
                        onClick: onAnotherUnit,
                        testId: 'rv-hub-another-unit-option',
                    },
                    {
                        title: 'A new model from scratch',
                        body: 'Starts again at step 1 with nothing carried over, and asks the questions again so you can change any of them.',
                        action: 'Start a new model from scratch',
                        onClick: onStartFresh,
                        testId: 'rv-hub-start-fresh',
                    },
                ]}
            />

            {/* The ways on — links, not buttons: none of them is the next step. */}
            <div className="pt-1">
                <PlanPageLinks
                    links={[
                        { label: 'Change the contract', icon: <Pencil size={14} />, onClick: onChangeContract, testId: 'rv-hub-link-contract' },
                        { label: 'All the plans we tested', icon: <ArrowLeftRight size={14} />, onClick: onAllPlans, testId: 'rv-hub-link-all-plans' },
                        // Two doors (owner, 25 Oct 2026): change this plan, or start a blank one.
                        { label: manual ? 'Change the plan you built' : 'Customise this plan', icon: <Hammer size={14} />, onClick: onBuild, testId: 'rv-hub-build' },
                        ...(onBuildNew ? [{ label: 'Build a plan yourself', icon: <Hammer size={14} />, onClick: onBuildNew, testId: 'rv-hub-build-new' }] : []),
                        { label: 'How villages work — the guide', icon: <BookOpen size={14} />, onClick: onGuide, testId: 'rv-hub-guide' },
                        { label: 'Rates and assumptions', icon: <BookOpen size={14} />, onClick: onAssumptions, testId: 'rv-hub-assumptions' },
                    ]}
                />
            </div>
        </div>
    );
};

export default RvHub;
